import { Container, Graphics, Rectangle, Text, TextStyle } from 'pixi.js';

/**
 * The triage console: the diegetic UI the case is actually written for.
 *
 * Narration is revealed like a terminal printout, and choices are shown as the
 * fictional commands the case content already defines (`/inspecter alerte-01`,
 * `/comparer domaine-lie`, ...) instead of generic buttons.
 */
export type LineKind = 'narration' | 'command' | 'ok' | 'bad' | 'system';

export interface ConsoleCommand {
  readonly id: string;
  /** The command line shown, e.g. "/inspecter alerte-01". */
  readonly text: string;
  /** Plain-language hint under the command. */
  readonly hint?: string;
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const COLORS: Record<LineKind, number> = {
  narration: 0x8fe3b0,
  command: 0xffd479,
  ok: 0x7fffb0,
  bad: 0xff8a7a,
  system: 0x5f8f7a,
};
const CHARS_PER_SECOND = 120;

interface LogEntry {
  readonly label: Text;
  readonly full: string;
  shown: number;
}

export class ConsolePanel {
  readonly view = new Container();
  private readonly logViewport = new Container();
  private readonly logContent = new Container();
  private readonly commandLayer = new Container();
  private readonly caret: Graphics;
  private readonly logMask = new Graphics();
  private entries: LogEntry[] = [];
  private onSelect: ((id: string) => void) | undefined;

  private readonly width: number;
  private readonly height: number;
  private logHeight: number;
  private scrollY = 0;
  private dragging = false;
  private lastPointerY = 0;
  private caretMs = 0;
  private commandsHeight = 0;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.logHeight = height - 28;

    this.view.addChild(
      new Graphics().rect(0, 0, width, height).fill({ color: 0x03090a, alpha: 0.93 }),
    );
    this.view.addChild(new Graphics().rect(0, 0, width, 1.5).fill({ color: 0x2f8f6a, alpha: 0.9 }));
    this.view.addChild(this.buildScanlines());

    this.logViewport.addChild(this.logContent);
    this.logViewport.eventMode = 'static';
    this.logViewport.hitArea = new Rectangle(0, 0, width, this.logHeight);
    this.view.addChild(this.logViewport);
    this.view.addChild(this.logMask);
    this.applyLogMask();

    this.caret = new Graphics().rect(0, 0, 7, 2).fill({ color: 0x8fe3b0 });
    this.logContent.addChild(this.caret);

    this.view.addChild(this.commandLayer);
    this.wireScroll();
  }

  private buildScanlines(): Graphics {
    const lines = new Graphics();
    for (let y = 0; y < this.height; y += 3) {
      lines.rect(0, y, this.width, 1).fill({ color: 0x000000, alpha: 0.16 });
    }
    return lines;
  }

  /**
   * Resizes the single log mask in place. It must be reused, never recreated:
   * a replaced mask stays in the display list as a plain filled rectangle and
   * would paint over the console.
   */
  private applyLogMask(): void {
    this.logMask
      .clear()
      .rect(10, 10, this.width - 20, this.logHeight - 10)
      .fill({ color: 0xffffff });
    this.logViewport.mask = this.logMask;
    this.logViewport.hitArea = new Rectangle(0, 0, this.width, this.logHeight);
  }

  private wireScroll(): void {
    this.logViewport.on('pointerdown', (event) => {
      this.dragging = true;
      this.lastPointerY = event.global.y;
    });
    this.logViewport.on('globalpointermove', (event) => {
      if (!this.dragging) return;
      const delta = this.lastPointerY - event.global.y;
      if (Math.abs(delta) > 0.5) this.scrollBy(delta);
      this.lastPointerY = event.global.y;
    });
    const release = (): void => {
      this.dragging = false;
    };
    this.logViewport.on('pointerup', release);
    this.logViewport.on('pointerupoutside', release);
    // Tapping the printout skips the typewriter.
    this.logViewport.on('pointertap', () => {
      this.skip();
    });
  }

  setOnSelect(callback: (id: string) => void): void {
    this.onSelect = callback;
  }

  get busy(): boolean {
    return this.entries.some((entry) => entry.shown < entry.full.length);
  }

  /** Resolves once every queued line has finished printing. */
  whenIdle(): Promise<void> {
    if (!this.busy) return Promise.resolve();
    return new Promise((resolve) => {
      const check = (): void => {
        if (this.busy) {
          setTimeout(check, 40);
          return;
        }
        resolve();
      };
      setTimeout(check, 40);
    });
  }

  skip(): void {
    for (const entry of this.entries) entry.shown = entry.full.length;
    this.refresh();
  }

  clearLog(): void {
    for (const entry of this.entries) entry.label.destroy();
    this.entries = [];
    this.scrollY = 0;
    this.refresh();
  }

  append(text: string, kind: LineKind = 'narration'): void {
    const clean = text.replace(/\r/g, '').trim();
    if (clean.length === 0) return;
    const prefix = kind === 'command' ? '> ' : '';
    const label = new Text({
      text: '',
      style: new TextStyle({
        fill: COLORS[kind],
        fontFamily: MONO,
        fontSize: kind === 'system' ? 11 : 12.5,
        lineHeight: kind === 'system' ? 15 : 17,
        wordWrap: true,
        wordWrapWidth: this.width - 28,
      }),
    });
    this.logContent.addChild(label);
    this.entries.push({ label, full: prefix + clean, shown: 0 });
    this.refresh();
  }

  setCommands(items: readonly ConsoleCommand[]): void {
    this.commandLayer.removeChildren().forEach((child) => {
      child.destroy({ children: true });
    });

    const itemHeight = 30;
    this.commandsHeight = items.length === 0 ? 0 : items.length * itemHeight + 12;
    this.logHeight = this.height - this.commandsHeight - 14;
    this.applyLogMask();

    items.forEach((item, index) => {
      const y = this.height - this.commandsHeight + 6 + index * itemHeight;
      const row = new Graphics()
        .roundRect(10, y, this.width - 20, itemHeight - 5, 4)
        .fill({ color: 0x0b2019, alpha: 0.95 })
        .stroke({ color: 0x2f8f6a, width: 1, alpha: 0.55 });
      row.eventMode = 'static';
      row.cursor = 'pointer';
      row.on('pointertap', () => {
        this.onSelect?.(item.id);
      });
      this.commandLayer.addChild(row);

      const command = new Text({
        text: `> ${item.text}`,
        style: new TextStyle({ fill: 0x7fffb0, fontFamily: MONO, fontSize: 12 }),
      });
      command.position.set(18, y + 3);
      this.commandLayer.addChild(command);

      if (item.hint !== undefined && item.hint.length > 0) {
        const hint = new Text({
          text: item.hint,
          style: new TextStyle({
            fill: 0x4f8f78,
            fontFamily: MONO,
            fontSize: 9,
            wordWrap: true,
            wordWrapWidth: this.width - 44,
          }),
        });
        hint.position.set(26, y + 16);
        this.commandLayer.addChild(hint);
      }
    });

    this.refresh();
  }

  private scrollBy(delta: number): void {
    const maxScroll = Math.max(0, this.contentHeight() - (this.logHeight - 20));
    this.scrollY = Math.max(0, Math.min(maxScroll, this.scrollY + delta));
    this.layout(false);
  }

  private contentHeight(): number {
    let y = 0;
    for (const entry of this.entries) y += entry.label.height + 8;
    return y;
  }

  private refresh(): void {
    this.layout(true);
  }

  /** Positions log lines; `autoScroll` pins the view to the newest output. */
  private layout(autoScroll: boolean): void {
    let y = 0;
    for (const entry of this.entries) {
      entry.label.position.set(14, y);
      y += entry.label.height + 8;
    }
    const visible = this.logHeight - 20;
    const maxScroll = Math.max(0, y - visible);
    if (autoScroll) this.scrollY = maxScroll;
    this.scrollY = Math.min(this.scrollY, maxScroll);
    this.logContent.y = 14 - this.scrollY;

    const last = this.entries.at(-1);
    if (last === undefined) {
      this.caret.visible = false;
      return;
    }
    this.caret.visible = true;
    this.caret.position.set(14, last.label.y + last.label.height + 2);
  }

  update(deltaMs: number): void {
    this.caretMs += deltaMs;
    this.caret.alpha = Math.floor(this.caretMs / 450) % 2 === 0 ? 0.9 : 0.15;

    const pending = this.entries.find((entry) => entry.shown < entry.full.length);
    if (pending === undefined) return;
    const step = Math.max(1, Math.round((CHARS_PER_SECOND * deltaMs) / 1000));
    pending.shown = Math.min(pending.full.length, pending.shown + step);
    pending.label.text = pending.full.slice(0, pending.shown);
    this.layout(true);
  }

  destroy(): void {
    this.view.destroy({ children: true });
  }
}
